/**
 * The Chart.js registrations for the whole app, in one place.
 *
 * `ChartJS.register` writes to a single global registry, so a register call is app-wide
 * wherever it lives. Every chart wrapper in this folder imports this module, which keeps each
 * wrapper self-sufficient (a test that renders one gets the registry it needs) while there is
 * one list to maintain.
 *
 * Only what a chart needs in order to draw belongs here: scales, elements, controllers and
 * plugins that do nothing unless a chart's options opt in. A plugin that changes how a chart
 * renders goes on that chart through its `plugins` prop, never here, because a global one runs
 * on every chart in the app.
 *
 * The Chart.js defaults follow the applied theme from here too (`applyChartThemeDefaults`), so
 * text, grid and tooltip colours are right on every chart, including the options a chart
 * leaves to the defaults.
 */
import {
    ArcElement,
    BarController,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    DoughnutController,
    Filler,
    Legend,
    LinearScale,
    LineController,
    LineElement,
    PieController,
    PointElement,
    TimeScale,
    Title,
    Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import zoomPlugin from 'chartjs-plugin-zoom';

import 'chartjs-adapter-date-fns';

import skipDetachedCanvasPlugin from './skipDetachedCanvasPlugin';
import type { ChartTheme } from './theme';
import { getDocumentChartTheme, subscribeAppliedTheme } from './theme';
import { solidSwatch } from './tooltipSwatch';

ChartJS.register(
    // Scales. TimeScale reads dates through the date-fns adapter imported above.
    CategoryScale,
    LinearScale,
    TimeScale,
    // Controllers. Mixed specs draw line datasets on a bar chart and the other way round,
    // so every chart needs the controllers of the dataset types it hosts.
    BarController,
    LineController,
    PieController,
    DoughnutController,
    // Elements.
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    // Core plugins, which every wrapper registered before. Legend and Tooltip are on by
    // default; a chart turns them off in its options. Filler and Title act only when enabled.
    // annotation and zoom are inert unless a chart declares annotations or zoom options.
    Filler,
    Legend,
    Tooltip,
    Title,
    annotationPlugin,
    zoomPlugin,
    // Guards every chart against a Chart.js 4.4 resize that fires after React unmounted the
    // canvas. It does nothing on an attached canvas.
    skipDetachedCanvasPlugin
);

// Every chart's tooltip swatch, so a hollow marker never shows as an empty ring.
ChartJS.defaults.plugins.tooltip.callbacks.labelColor = solidSwatch;

/**
 * Points the Chart.js defaults at `theme`: text, grid and axis lines, tooltips and the font.
 * Then redraws every live chart without animation, so a chart whose own options did not
 * change (it left those colours to the defaults) switches with the rest of the page.
 */
export function applyChartThemeDefaults(
    theme: ChartTheme,
    liveCharts: Iterable<Pick<ChartJS, 'update'>> = Object.values(ChartJS.instances)
): void {
    const { defaults } = ChartJS;
    defaults.color = theme.text;
    defaults.borderColor = theme.grid;
    defaults.scale.grid.color = theme.grid;
    // `defaults.scale` is typed as every scale type at once; the radial one has no border.
    if ('border' in defaults.scale) {
        defaults.scale.border.color = theme.axis;
    }
    defaults.font.family = theme.fontFamily;

    const tooltip = defaults.plugins.tooltip;
    tooltip.backgroundColor = theme.tooltip.bg;
    tooltip.titleColor = theme.tooltip.text;
    tooltip.bodyColor = theme.tooltip.text;
    tooltip.footerColor = theme.tooltip.text;
    tooltip.borderColor = theme.tooltip.border;
    tooltip.borderWidth = 1;
    // Chart.js asks for bold here, which a single-weight BoxedRound face cannot draw.
    tooltip.titleFont = { family: theme.strongFontFamily, weight: 'normal' };
    tooltip.footerFont = { family: theme.strongFontFamily, weight: 'normal' };

    for (const chart of liveCharts) {
        chart.update('none');
    }
}

let unsubscribeTheme: (() => void) | null = null;

/**
 * Applies the current theme to the defaults and follows every later switch. Runs once, just
 * after the first chart module loads: before any chart draws, and subscribed ahead of the
 * charts themselves, so the defaults have switched by the time a chart re-renders.
 */
export function syncChartDefaultsWithTheme(): void {
    const theme = getDocumentChartTheme();
    applyChartThemeDefaults(theme);
    if (unsubscribeTheme) return;
    // A canvas does not wait for a web font: a face no DOM text has used yet would draw the
    // first tooltip title in the fallback. Loading it up front avoids that.
    if (typeof document !== 'undefined' && 'fonts' in document) {
        document.fonts.load(`12px ${theme.strongFontFamily}`).catch(() => undefined);
    }
    unsubscribeTheme = subscribeAppliedTheme(() =>
        applyChartThemeDefaults(getDocumentChartTheme())
    );
}

// Deferred past module evaluation: index.tsx imports the app, and with it this module, before
// the global stylesheets, and in dev those inject their tokens as their own modules evaluate.
queueMicrotask(syncChartDefaultsWithTheme);
